import { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';

interface KanbanColumn {
  id: string;
  title: string;
  icon?: string;
  cards: any[];
}

interface KanbanColumnsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (columns: KanbanColumn[]) => void;
  columns: KanbanColumn[];
  title?: string;
}

export default function KanbanColumnsModal({
  isOpen,
  onClose,
  onSave,
  columns,
  title = 'Editar Colunas do Kanban',
}: KanbanColumnsModalProps) {
  const [editedColumns, setEditedColumns] = useState<KanbanColumn[]>([]);

  useEffect(() => {
    setEditedColumns(JSON.parse(JSON.stringify(columns || [])));
  }, [columns, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(editedColumns);
    onClose();
  };

  const handleAddColumn = () => {
    setEditedColumns([
      ...editedColumns,
      {
        id: `col-${Date.now()}`,
        title: 'Nova Coluna',
        icon: '📋',
        cards: [],
      },
    ]);
  };

  const handleDeleteColumn = (index: number) => {
    setEditedColumns(editedColumns.filter((_, i) => i !== index));
  };

  const handleUpdateColumn = (index: number, field: string, value: string) => {
    const newColumns = [...editedColumns];
    newColumns[index] = { ...newColumns[index], [field]: value };
    setEditedColumns(newColumns);
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <form onSubmit={handleSubmit} className="flex flex-col">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>
              Configure as colunas do seu quadro Kanban
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto py-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium">Colunas</h3>
              <Button
                type="button"
                onClick={handleAddColumn}
                size="sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Coluna
              </Button>
            </div>

            <div className="space-y-3">
              {editedColumns.map((column, index) => (
                <div key={index} className="p-4 border-2 rounded-lg bg-muted/30">
                  <div className="flex items-center gap-3">
                    <Input
                      type="text"
                      placeholder="Ícone"
                      value={column.icon || ''}
                      onChange={(e) => handleUpdateColumn(index, 'icon', e.target.value)}
                      className="w-16 text-center"
                    />
                    <Input
                      type="text"
                      placeholder="Nome da coluna"
                      value={column.title}
                      onChange={(e) => handleUpdateColumn(index, 'title', e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteColumn(index)}
                      title="Excluir coluna"
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="mt-2">
                    <Badge variant="outline" className="text-xs">
                      {column.cards.length} {column.cards.length === 1 ? 'card' : 'cards'}
                    </Badge>
                  </div>
                </div>
              ))}

              {editedColumns.length === 0 && (
                <p className="text-center text-muted-foreground py-8">
                  Nenhuma coluna criada. Clique em "Adicionar Coluna" para começar.
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancelar
            </Button>
            <Button type="submit">
              Salvar Colunas
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
